import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  DollarSign,
  ExternalLink,
  Loader2,
  MapPin,
  Phone,
  ZoomIn,
} from 'lucide-react';

import { Badge } from '../ui/badge';
import SEO from '../SEO';
import { getStaticData } from '../../hooks/useStaticData';
import {
  combineSchemas,
  getBreadcrumbSchema,
  getEventPriceClauses,
  getEventSchema,
} from '../../utils/structuredData';

import {
  eventPath,
  eventTypeColor,
  eventTypeLabel,
  formatDate,
  formatTime,
  checkInWindow,
  isUpcoming,
  mapsUrl,
} from './eventDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const DEFAULT_REGISTRATION_PHONE = '(352) 428-0086';

// Build-time snapshot of upcoming events, so the react-snap prerender (which
// blocks API calls) still renders the event and its JSON-LD.
const findStaticEvent = (eventId) =>
  (getStaticData('events')?.items || []).find((event) => event.id === eventId) || null;

const metaDescription = (event) => {
  const text = (event.description || '').replace(/\s+/g, ' ').trim();
  const summary = `${formatDate(event.date)} at ${(event.location || '').split(',')[0].trim()}. ${text}`;
  return summary.length > 160 ? `${summary.slice(0, 157).trimEnd()}...` : summary;
};

const BackLink = () => (
  <a
    href="/events"
    className="inline-flex items-center gap-2 text-sm font-semibold text-red-700 hover:text-red-800 hover:underline dark:text-red-300"
  >
    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
    All events
  </a>
);

const DetailRow = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
    <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" aria-hidden="true" />
    <div>{children}</div>
  </div>
);

const Registration = ({ event }) => {
  if (!isUpcoming(event)) {
    return (
      <p className="rounded-xl bg-gray-100 p-4 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
        This event has already taken place.
      </p>
    );
  }

  if (event.registration_url) {
    return event.registration_open ? (
      <a
        href={event.registration_url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-red-700"
      >
        <ExternalLink className="h-4 w-4" aria-hidden="true" />
        Register Now
      </a>
    ) : (
      <p className="rounded-xl bg-gray-100 p-4 font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
        Registration is closed.
      </p>
    );
  }

  const phone = getStaticData('businessInfo')?.direct_line_phone || DEFAULT_REGISTRATION_PHONE;
  const isTournament = event.event_type === 'tournament';
  const checkIn = checkInWindow(event) || `from ${formatTime(event.date)}`;

  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-5 dark:border-red-900 dark:bg-red-950/40">
      <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">How to register</h2>
      <p className="mt-2 text-gray-700 dark:text-gray-300">
        {isTournament ? 'Pre-registration is encouraged' : 'Pre-register by phone'}: call Hanshi Jim Rivera at{' '}
        <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`} className="font-semibold text-red-700 underline dark:text-red-300">
          {phone}
        </a>
        .
      </p>
      {isTournament && (
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Walk-up registration is also available at the door on tournament day. Check-in and registration: {checkIn}.
        </p>
      )}
      <a
        href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-red-700"
      >
        <Phone className="h-4 w-4" aria-hidden="true" />
        Call {phone}
      </a>
    </div>
  );
};

const EventDetail = ({ event }) => {
  const prices = getEventPriceClauses(event);
  const path = eventPath(event);

  return (
    <article className="mt-6 grid gap-8 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      {event.image_url && (
        <div>
          <a
            href={event.image_url}
            target="_blank"
            rel="noopener noreferrer"
            className="group block overflow-hidden rounded-2xl bg-gray-900 p-2 shadow-lg"
            aria-label={`Open the full-size ${event.title} poster`}
          >
            <img src={event.image_url} alt={`${event.title} poster`} className="h-auto w-full rounded-xl" />
          </a>
          <a
            href={event.image_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-red-700 hover:underline dark:text-gray-400"
          >
            <ZoomIn className="h-4 w-4" aria-hidden="true" />
            View full-size poster
          </a>
        </div>
      )}

      <div className={event.image_url ? '' : 'md:col-span-2'}>
        <div className="flex flex-wrap gap-2">
          <Badge className={eventTypeColor(event)}>{eventTypeLabel(event)}</Badge>
          {!isUpcoming(event) && <Badge variant="secondary">Past Event</Badge>}
        </div>
        <h1 className="mt-3 text-3xl font-bold text-gray-900 dark:text-gray-100 sm:text-4xl">{event.title}</h1>

        <div className="mt-6 space-y-3">
          <DetailRow icon={CalendarDays}>{formatDate(event.date)}</DetailRow>
          <DetailRow icon={Clock}>
            {formatTime(event.date)}
            {event.end_date && ` - ${formatTime(event.end_date)}`}
          </DetailRow>
          {event.location && (
            <DetailRow icon={MapPin}>
              <p>{event.location}</p>
              <a
                href={mapsUrl(event.location)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-red-700 hover:underline dark:text-red-300"
              >
                Open in Google Maps
              </a>
            </DetailRow>
          )}
          {prices.length > 0 && (
            <DetailRow icon={DollarSign}>
              <ul>
                {prices.map((price, index) => (
                  <li key={index}>{price}</li>
                ))}
              </ul>
            </DetailRow>
          )}
        </div>

        <div className="mt-6">
          <Registration event={event} />
        </div>

        {event.description && (
          <section className="mt-8">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">About this event</h2>
            <p className="mt-2 whitespace-pre-line leading-relaxed text-gray-700 dark:text-gray-300">
              {event.description}
            </p>
          </section>
        )}

        <SEO
          title={event.title}
          description={metaDescription(event)}
          canonical={path}
          ogImage={event.image_url || undefined}
          jsonLd={combineSchemas(
            getBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Events', path: '/events' },
              { name: event.title, path },
            ]),
            getEventSchema(event, path)
          )}
        />
      </div>
    </article>
  );
};

const EventDetailPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(() => findStaticEvent(eventId));
  const [status, setStatus] = useState('loading'); // loading | ready | not_found | error

  useEffect(() => {
    let cancelled = false;
    setEvent(findStaticEvent(eventId));
    setStatus('loading');
    fetch(`${API}/events/${encodeURIComponent(eventId)}`)
      .then(async (response) => {
        if (cancelled) return;
        if (response.status === 404) {
          setEvent(null);
          setStatus('not_found');
          return;
        }
        if (!response.ok) throw new Error('Failed to load event');
        const data = await response.json();
        if (!cancelled) {
          setEvent(data);
          setStatus('ready');
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  let body;
  if (event) {
    body = <EventDetail event={event} />;
  } else if (status === 'loading') {
    body = (
      <div className="py-24 text-center">
        <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-red-600" />
        <p className="text-gray-600">Loading event...</p>
      </div>
    );
  } else {
    body = (
      <div className="py-24 text-center">
        <SEO title="Event not found" canonical="/events" noindex />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {status === 'not_found' ? 'Event not found' : "We couldn't load this event"}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {status === 'not_found'
            ? 'This event may have been removed. See the events page for what is coming up.'
            : 'Please try again in a moment.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100 px-4 py-8">
      <div className="mx-auto max-w-5xl">
        <BackLink />
        {body}
      </div>
    </div>
  );
};

export default EventDetailPage;
